import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelectionStore } from "../stores/selectionStore";
import { getViewerActions, getViewerState } from "../stores/viewerStore";
import {
  bodyInspectorOrientationAxesVisible,
  clearMeshInspectorBoundingBoxOverlays,
  renderBodyInspectorBoundingBox,
  setBodyInspectorOrientationAxes,
} from "../selected-mesh-visibility/inspectorOverlays";

export type BodyVisibilityState = {
  focus: boolean;
  boundingBox: boolean;
  originMarker: boolean;
};

const defaultState = (): BodyVisibilityState => ({
  focus: false,
  boundingBox: false,
  originMarker: false,
});

let rememberedBodyVisibilityState: BodyVisibilityState = defaultState();

export type UseBodyVisibilityControlsArgs = {
  bodyUuid: string | null;
  enabled?: boolean;
  initialState?: Partial<BodyVisibilityState>;
  onStateChange?: (state: BodyVisibilityState) => void;
};

export type UseBodyVisibilityControlsResult = {
  state: BodyVisibilityState;
  disabled: boolean;
  focusDisabled: boolean;
  toggleFocus: () => void;
  toggleBoundingBox: () => void;
  toggleOriginMarker: () => void;
  clearAllOverlays: () => void;
  reset: () => void;
};

export function useBodyVisibilityControls({
  bodyUuid,
  enabled = true,
  initialState,
  onStateChange,
}: UseBodyVisibilityControlsArgs): UseBodyVisibilityControlsResult {
  const focusModeActive = useSelectionStore((s) => s.focusModeActive);
  const focusBodyName = useSelectionStore((s) => s.focusBodyName);
  const focusMeshUuid = useSelectionStore((s) => s.focusMeshUuid);
  const focusCollisionGeomUuid = useSelectionStore((s) => s.focusCollisionGeomUuid);
  const [state, setStateInternal] = useState<BodyVisibilityState>(() => ({
    ...defaultState(),
    ...rememberedBodyVisibilityState,
    ...initialState,
  }));

  const disabled = !enabled || !bodyUuid;
  const focusDisabled = disabled;

  const ensureVisualOn = useCallback(() => {
    if (!getViewerState().showVisual) {
      getViewerActions().toggleShowVisual();
    }
  }, []);

  const prevOriginRef = useRef<string | null>(null);
  const lastBodyUuidRef = useRef<string | null>(null);
  const focusOwnedRef = useRef(false);

  lastBodyUuidRef.current = bodyUuid;

  const setState = useCallback(
    (patch: Partial<BodyVisibilityState>) => {
      setStateInternal((prev) => {
        const next = { ...prev, ...patch };
        rememberedBodyVisibilityState = next;
        onStateChange?.(next);
        return next;
      });
    },
    [onStateChange]
  );

  const isFocusActiveForBody = useMemo(
    () =>
      !!bodyUuid &&
      !!focusModeActive &&
      focusBodyName === bodyUuid &&
      !focusMeshUuid &&
      !focusCollisionGeomUuid,
    [bodyUuid, focusModeActive, focusBodyName, focusMeshUuid, focusCollisionGeomUuid]
  );

  useEffect(() => {
    if (!bodyUuid) return;
    if (rememberedBodyVisibilityState.focus && !isFocusActiveForBody) {
      useSelectionStore.getState().setFocusMode(true, bodyUuid);
    }
    const nextFocus = rememberedBodyVisibilityState.focus || isFocusActiveForBody;
    setStateInternal((prev) => (prev.focus === nextFocus ? prev : { ...prev, focus: nextFocus }));
  }, [bodyUuid, isFocusActiveForBody]);

  useEffect(() => {
    if (!bodyUuid) return;
    const hasAxes = bodyInspectorOrientationAxesVisible(bodyUuid);
    const nextOriginMarker = rememberedBodyVisibilityState.originMarker || hasAxes;
    setStateInternal((prev) =>
      prev.originMarker === nextOriginMarker ? prev : { ...prev, originMarker: nextOriginMarker }
    );
  }, [bodyUuid]);

  useEffect(() => {
    if (disabled || !bodyUuid || !state.boundingBox) {
      clearMeshInspectorBoundingBoxOverlays();
      return;
    }
    renderBodyInspectorBoundingBox(bodyUuid);
  }, [disabled, bodyUuid, state.boundingBox]);

  useEffect(() => {
    const prev = prevOriginRef.current;
    if (prev && bodyUuid && prev !== bodyUuid) {
      setBodyInspectorOrientationAxes(prev, false);
    }

    if (disabled || !bodyUuid || !state.originMarker) {
      if (bodyUuid) setBodyInspectorOrientationAxes(bodyUuid, false);
      prevOriginRef.current = bodyUuid || null;
      return;
    }
    setBodyInspectorOrientationAxes(bodyUuid, true);
    prevOriginRef.current = bodyUuid;
  }, [disabled, bodyUuid, state.originMarker]);

  useEffect(() => {
    return () => {
      clearMeshInspectorBoundingBoxOverlays();
      const last = lastBodyUuidRef.current;
      if (last) setBodyInspectorOrientationAxes(last, false);
      if (focusOwnedRef.current) {
        try {
          useSelectionStore.getState().clearFocusMode();
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  useEffect(() => {
    focusOwnedRef.current = false;
  }, [bodyUuid]);

  const toggleFocus = useCallback(() => {
    if (focusDisabled) return;
    const next = !state.focus;
    if (next) {
      ensureVisualOn();
      useSelectionStore.getState().setFocusMode(true, bodyUuid);
    } else {
      useSelectionStore.getState().clearFocusMode();
    }
    focusOwnedRef.current = next;
    setState({ focus: next });
  }, [bodyUuid, focusDisabled, ensureVisualOn, setState, state.focus]);

  const toggleBoundingBox = useCallback(() => {
    if (disabled) return;
    const next = !state.boundingBox;
    if (next) ensureVisualOn();
    setState({ boundingBox: next });
  }, [disabled, ensureVisualOn, state.boundingBox, setState]);

  const toggleOriginMarker = useCallback(() => {
    if (disabled) return;
    const next = !state.originMarker;
    if (next) ensureVisualOn();
    setState({ originMarker: next });
  }, [disabled, ensureVisualOn, state.originMarker, setState]);

  const clearAllOverlays = useCallback(() => {
    clearMeshInspectorBoundingBoxOverlays();
    if (bodyUuid) setBodyInspectorOrientationAxes(bodyUuid, false);
    setStateInternal((prev) => {
      const next = { ...prev, boundingBox: false, originMarker: false };
      rememberedBodyVisibilityState = next;
      onStateChange?.(next);
      return next;
    });
  }, [bodyUuid, onStateChange]);

  const reset = useCallback(() => {
    clearAllOverlays();
    if (focusOwnedRef.current) {
      try {
        useSelectionStore.getState().clearFocusMode();
      } catch {
        /* ignore */
      }
      focusOwnedRef.current = false;
    }
    setStateInternal(() => {
      const next = { ...defaultState(), ...initialState };
      rememberedBodyVisibilityState = next;
      onStateChange?.(next);
      return next;
    });
  }, [clearAllOverlays, initialState, onStateChange]);

  return {
    state,
    disabled,
    focusDisabled,
    toggleFocus,
    toggleBoundingBox,
    toggleOriginMarker,
    clearAllOverlays,
    reset,
  };
}
